/**
 * One-shot readback of the rendered frame, inside the render turn.
 *
 * WHY this exists at all: app.ts creates the graphics device WITHOUT
 * preserveDrawingBuffer, and PlayCanvas forwards initOptions straight into
 * getContext, so the browser is entitled to discard the WebGL drawing buffer the
 * instant it composites a frame. A click handler always runs in a task AFTER
 * that composite — which is why the old Toolbar.screenshot() shipped a fully
 * transparent PNG on every platform (verified in prod: 1100x720, litRatio 0,
 * meanBrightness 0).
 *
 * WHY NOT just set preserveDrawingBuffer: it makes the driver copy EVERY frame,
 * forever, to save a few lines in one click handler. That is the mobile frame
 * budget Phase 4 fought for. Do not "simplify" this module by turning it on.
 *
 * WHY the render is synchronous rather than a deferred 'frameend' capture:
 * WebKit's user-gesture token is a call-stack property, not Chromium's 5-second
 * window (UserGestureIndicator::maximumIntervalForUserGestureForwarding is 1s
 * and is captured at requestAnimationFrame REGISTRATION time — and PlayCanvas
 * registers its rAF from inside its own tick, so it carries no token). A capture
 * deferred to the next frame therefore has ZERO user activation on iOS, and
 * navigator.share() rejects NotAllowedError with no sheet and no error the user
 * can see. AppBase.render() is synchronous end to end (canvas resync, device
 * frameStart/backbuffer realloc, the whole frame graph, then 'postrender'), so
 * calling it from the click gives us a live drawing buffer and an intact
 * gesture in the same task.
 *
 * The readback rides a ONE-SHOT 'postrender' handler that is detached in a
 * finally, so the feature costs exactly nothing per frame while idle. That is
 * also the hook mount.ts's paint probe already uses successfully in prod.
 *
 * CONSEQUENCE OF THE SINGLE TASK, stated plainly because it is not fixable
 * here: nothing can be painted between the tap and the delivery. A paint needs
 * the task to end, and ending the task is exactly what would destroy the
 * gesture token. So every millisecond of this module is a millisecond of frozen
 * UI with no feedback — which is why the capture is bounded in size below, why
 * the blank probes are 1x1 reads rather than full-frame ones, and why the
 * offscreen context asks for willReadFrequently.
 */

import type * as pc from 'playcanvas';

import { isWebGpu, readSceneRect, sceneTargetOf } from './frame-source';

/** Backbuffer clear colour — mirrors the camera clearColor in app.ts and --bg. */
const CLEAR_CSS = '#070707';

/**
 * Same threshold as the paint probe in mount.ts: r+g+b on the 0..765 scale.
 * Below it the pixel is indistinguishable from the #070707 clear.
 */
const LIT_SUM = 45;

/**
 * Upper bound on the SAVED image's long edge.
 *
 * Not cosmetic. The drawing buffer is NOT 1x CSS pixels: AdaptiveQualityController
 * writes device.maxPixelRatio = max(0.5, dpr * rtScale) in its constructor
 * (quality/adaptive-quality.ts), so an iPhone XS (dpr 3) runs 1.95 at its
 * starting rung and 3.0 at the top rung — a 1125x2436 = 2.7 Mpx buffer. Encoding
 * that as PNG costs, measured in Chrome on noisy photo-like content, ~53ms per
 * capture on a desktop CPU and holds ~35MB of transient copies alive at once
 * (canvas + data URL string + binary string + Uint8Array + File + the rescue
 * overlay's decoded <img>). Both land inside a frozen gesture on a 4GB phone.
 * 1920 is also the ceiling every messaging pipeline recompresses to anyway, so
 * the bound costs nothing a recipient would ever see. NEVER upscales, and never
 * touches the RENDER resolution — raising maxPixelRatio mid-gesture would
 * reallocate the backbuffer and every CameraFrame render target, which is the
 * spike Phase 4 exists to avoid.
 */
const MAX_CAPTURE_EDGE = 1920;

/**
 * Edge of the thumbnail the blank test measures. Deliberately a box-filtered
 * MIP rather than a handful of point samples: every pixel of the frame
 * contributes to some cell, so a mostly-black view with one small lit object
 * cannot be mistaken for an empty buffer the way a point grid does (a 5x5 grid
 * walked straight past a 60px lit patch in testing). Measured cost of the
 * drawImage + one 256-pixel getImageData at 1125x2436: 0.00-0.02ms.
 */
const PROBE_EDGE = 16;

/**
 * How far apart the darkest and brightest probe cells must be before the frame
 * counts as real content. An unwritten buffer is bit-flat; even a night scene
 * is not.
 */
const FLAT_SPREAD = 6;

type GlDevice = pc.GraphicsDevice & { contextLost?: boolean; gl?: WebGL2RenderingContext };
type XrLike = { active?: boolean } | undefined;

export class CaptureError extends Error {}

/** willReadFrequently because this canvas is probed and then encoded, both of
 *  which are readbacks; without it Chrome logs a console warning and each
 *  getImageData can force a GPU sync inside the frozen gesture. */
function opaque2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    if (!ctx) throw new CaptureError('no 2d context');
    return ctx;
}

/**
 * True when the canvas is actually what gets drawn. tick() bails on contextLost
 * and both resize paths bail on XR, where the headset — not the canvas — is the
 * render target, so a readback there is meaningless rather than merely ugly.
 */
function capturable(app: pc.AppBase): boolean {
    const dev = app.graphicsDevice as GlDevice;
    return !dev.contextLost && !(app.xr as XrLike)?.active;
}

/**
 * Is this an UNWRITTEN drawing buffer rather than a dark picture?
 *
 * The signature to look for is UNIFORMITY, not darkness. When the readback fails
 * we get either the flat #070707 this module pre-fills or a flat black from a
 * zeroed readPixels — every cell identical. A real frame, however dim, varies.
 *
 * This distinction is the whole point. The first cut used mount.ts's paint-probe
 * threshold (three points on the centre scanline, r+g+b > 45) as a hard
 * refusal-to-deliver, which meant any view whose middle scanline was darker than
 * ~6% per channel — a shadowed corner, an unlit floor, a night exterior, a
 * close-up of dark furniture — got "Could not save image" on every tap, forever,
 * while a perfectly good frame sat on screen. The paint probe can use darkness
 * as a proxy for "has anything appeared yet" because a false negative there
 * merely waits one more frame; here a false negative destroys the deliverable.
 *
 * Used for BOTH decisions: whether to spend the readPixels fallback (a false
 * positive there costs work, never correctness) and whether to refuse delivery.
 */
export function looksUnwritten(canvas: HTMLCanvasElement): boolean {
    const mip = document.createElement('canvas');
    mip.width = PROBE_EDGE;
    mip.height = PROBE_EDGE;
    const ctx = mip.getContext('2d', { alpha: false, willReadFrequently: true });
    if (!ctx) return false;   // cannot prove it is empty, so deliver it
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, PROBE_EDGE, PROBE_EDGE);
    const d = ctx.getImageData(0, 0, PROBE_EDGE, PROBE_EDGE).data;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < d.length; i += 4) {
        const sum = d[i] + d[i + 1] + d[i + 2];
        if (sum > LIT_SUM) return false;           // something is lit — real frame
        if (sum < min) min = sum;
        if (sum > max) max = sum;
    }
    return max - min <= FLAT_SPREAD;
}

/**
 * Raw fallback readback, for WebKit builds that have historically returned a
 * blank drawImage() from a WebGL canvas. This is the path already proven in
 * production on iPhone Safari AND iPhone Chrome by mount.ts's paint probe.
 *
 * gl.readPixels row 0 is the BOTTOM scanline while ImageData row 0 is the top,
 * hence the row-reversing copy — done with one TypedArray.set per row rather
 * than per byte, because at 2.7 Mpx a per-byte loop is 11 million iterations
 * inside a frozen gesture. Alpha is then pinned to 255 in a single 32-bit pass:
 * the stored buffer is premultiplied, and pinning makes premultiplied and
 * straight identical instead of guessing which one the encoder wants.
 */
function readPixelsCanvas(gl: WebGL2RenderingContext, w: number, h: number): HTMLCanvasElement {
    // At 'postrender' the last pass targeted device.backBuffer, whose WebGL impl
    // carries a null framebuffer, so the default FBO is already bound. Restore
    // the raw GL binding rather than touching device.renderTarget, so
    // PlayCanvas's cached state stays coherent.
    const prev = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    // Uint8Array, not Uint8ClampedArray: the WebGL spec requires exactly that
    // view type for an UNSIGNED_BYTE read, and some drivers do raise
    // INVALID_OPERATION for the clamped one. ImageData.data.set() takes it fine.
    const src = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, src);
    gl.bindFramebuffer(gl.FRAMEBUFFER, prev);

    const raw = document.createElement('canvas');
    raw.width = w;
    raw.height = h;
    const ctx = opaque2d(raw);
    const img = ctx.createImageData(w, h);
    const stride = w * 4;
    for (let y = 0; y < h; y++) {
        img.data.set(src.subarray((h - 1 - y) * stride, (h - y) * stride), y * stride);
    }
    // Every browser platform is little-endian, so the alpha byte is the high
    // byte of each 32-bit pixel; OR-ing is ~4x cheaper than four byte writes.
    const words = new Uint32Array(img.data.buffer);
    for (let i = 0; i < words.length; i++) words[i] |= 0xff000000;
    ctx.putImageData(img, 0, 0);
    return raw;
}

/** Copy the live drawing buffer into a canvas we own, bounded by
 *  MAX_CAPTURE_EDGE. Callers must be inside the render turn — this is only ever
 *  called from the 'postrender' handler below. */
function copyDrawingBuffer(app: pc.AppBase, canvas: HTMLCanvasElement): HTMLCanvasElement {
    // canvas.width/height are the DRAWING-BUFFER dimensions (see
    // MAX_CAPTURE_EDGE — they are NOT 1x CSS pixels). They are guaranteed to
    // match the frame just drawn: render() re-syncs the canvas size and
    // reallocates the backbuffer before any pass runs, so an
    // AdaptiveQualityController resolution step (it writes maxPixelRatio from
    // 'framerender', strictly earlier) is fully absorbed by the same render()
    // we are reading.
    const srcW = canvas.width;
    const srcH = canvas.height;
    const k = Math.min(1, MAX_CAPTURE_EDGE / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * k));
    const h = Math.max(1, Math.round(srcH * k));

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = opaque2d(out);
    ctx.imageSmoothingQuality = 'high';

    // Belt and braces: the compose shader writes the scene RT's alpha straight
    // through and the camera clears that RT opaque, so every pixel should
    // already be solid — but the WebGL context is alpha:true/premultiplied by
    // engine default and on screen the canvas sits on a CSS background, so any
    // stray sub-1 alpha would punch a hole the user never saw.
    ctx.fillStyle = CLEAR_CSS;
    ctx.fillRect(0, 0, w, h);
    // drawImage of a canvas element uses its PRESENTED image, so GL's
    // bottom-left origin and premultiplication are already resolved — unlike
    // readPixels below, which needs the manual flip.
    ctx.drawImage(canvas, 0, 0, srcW, srcH, 0, 0, w, h);

    if (looksUnwritten(out)) {
        const gl = (app.graphicsDevice as GlDevice).gl;
        if (gl) {
            const raw = readPixelsCanvas(gl, srcW, srcH);
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(raw, 0, 0, srcW, srcH, 0, 0, w, h);
        }
    }
    return out;
}

/**
 * Render out of band and read the result back in the SAME task, so the caller
 * still holds the click's transient user activation and can go straight on to
 * navigator.share(). Costs one extra frame of GPU time per capture, and nothing
 * at all between captures.
 *
 * Throws CaptureError when the device is not presentable or when both readback
 * paths came back as an unwritten buffer — we would rather show a translated
 * failure than hand the user a black rectangle, which is the "button did
 * nothing" bug in a new costume.
 */
export function captureFrame(app: pc.AppBase, canvas: HTMLCanvasElement): HTMLCanvasElement {
    if (!capturable(app)) throw new CaptureError('device not presentable');

    // A box, not two locals: TypeScript's flow analysis does not track writes
    // made inside a closure, so a plain `let out` would be narrowed to null and
    // then to never — and the checks below would typecheck against nothing.
    const result: { out: HTMLCanvasElement | null; failure: unknown } = { out: null, failure: null };

    // 'postrender' fires at the tail of render(), after the last GL draw and
    // before the browser composites. Deliberately NOT 'frameend': that fires
    // from tick() whether or not render() ran (the render is gated on
    // autoRender || renderNextFrame, and the editor-preview message handler in
    // mount.ts parks the loop with autoRender = false), so a frameend capture
    // can legitimately read a stale or blank buffer. postrender only exists
    // inside render(), so it is self-gating.
    const onPostRender = () => {
        if (result.out || result.failure) return; // one-shot even if something re-enters
        try {
            result.out = copyDrawingBuffer(app, canvas);
        } catch (err) {
            result.failure = err;
        }
    };

    app.on('postrender', onPostRender);
    try {
        app.render();
    } finally {
        // Every exit path, including a throw from render() itself — otherwise
        // handlers accumulate across repeated camera taps.
        app.off('postrender', onPostRender);
    }

    if (result.failure) throw result.failure;
    if (!result.out) throw new CaptureError('postrender never fired');
    if (looksUnwritten(result.out)) throw new CaptureError('captured frame is an unwritten buffer');
    return result.out;
}

/**
 * Encode to PNG, synchronously, returning both representations the delivery
 * ladder needs so the image is only ever encoded once.
 *
 * toDataURL (not toBlob): toBlob's callback is a fresh task, which on WebKit has
 * no forwarded gesture token — one of the four reasons the old screenshot path
 * never delivered anything on iOS. base64 → Uint8Array → File is synchronous
 * too, so the whole chain stays inside the click.
 *
 * A real File rather than a Blob: navigator.share needs one, and Safari picks
 * the download filename off File.name (blob: URLs have historically been saved
 * as "unknown.txt"). The data: URL is what the rescue overlay shows.
 */
export function encodePng(canvas: HTMLCanvasElement, filename: string): { file: File; dataUrl: string } {
    const dataUrl = canvas.toDataURL('image/png');
    // WebKit is documented to return the literal string "data:," when it
    // refuses to encode, rather than throwing — a `indexOf(',')` check passes
    // that happily and hands the ladder a 0-byte File to share. Validate the
    // payload, not the separator.
    if (!dataUrl.startsWith('data:image/png;base64,') || dataUrl.length < 64) {
        throw new CaptureError('encode failed');
    }
    const bin = atob(dataUrl.slice(dataUrl.indexOf(',') + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { file: new File([bytes], filename, { type: 'image/png' }), dataUrl };
}

/**
 * WebGPU variant. The presented canvas is unreadable there (see
 * frame-source.ts), so the frame is rendered and then read back from the
 * CameraFrame's scene target — an async GPU copy. That costs the click's user
 * activation: by the time the pixels arrive, navigator.share() on iOS will
 * reject NotAllowedError and the delivery ladder in toolbar.ts drops to the
 * rungs that need no activation (download on desktop, the hold-to-save overlay
 * on phones). Same bounded size and the same unwritten-buffer refusal as the
 * synchronous path.
 *
 * On WebGL this simply forwards to captureFrame(), so callers can pick by
 * backend without two code paths of their own.
 */
export async function captureFrameAsync(app: pc.AppBase, canvas: HTMLCanvasElement): Promise<HTMLCanvasElement> {
    if (!isWebGpu(app)) return captureFrame(app, canvas);
    if (!capturable(app)) throw new CaptureError('device not presentable');
    const st = sceneTargetOf(app);
    if (!st) throw new CaptureError('scene target unavailable');

    // Render once so the scene target holds the current view (autoRender may
    // be parked). The copy has to be recorded INSIDE that frame — the engine's
    // WebGPU readback rides the frame's command encoder and a read issued
    // after render() returns comes back all zeros (measured) — so it is
    // issued from a one-shot 'postrender', exactly like the WebGL path.
    const srcW = st.texture.width;
    const srcH = st.texture.height;
    const rgba = await new Promise<Uint8ClampedArray<ArrayBuffer>>((resolve, reject) => {
        const onPost = () => { readSceneRect(app, 0, 0, srcW, srcH).then(resolve, reject); };
        app.once('postrender', onPost);
        try {
            app.render();
        } catch (err) {
            app.off('postrender', onPost);
            reject(err);
        }
    });

    const full = document.createElement('canvas');
    full.width = srcW;
    full.height = srcH;
    opaque2d(full).putImageData(new ImageData(rgba, srcW, srcH), 0, 0);

    const k = Math.min(1, MAX_CAPTURE_EDGE / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * k));
    const h = Math.max(1, Math.round(srcH * k));
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = opaque2d(out);
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = CLEAR_CSS;
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(full, 0, 0, srcW, srcH, 0, 0, w, h);

    if (looksUnwritten(out)) throw new CaptureError('captured frame is an unwritten buffer');
    return out;
}
